import { Role } from "@/graphql";

export type AppRole = "FINANCE" | "OPERATOR" | "ADMIN";

export const APP_ROLE_LABELS: Record<AppRole, string> = {
  FINANCE: "Finance",
  OPERATOR: "Operator",
  ADMIN: "Admin",
};

export const PERMITTED_BACKEND_ROLES = new Set<Role>([
  Role.Admin,
  Role.Operator,
  Role.Finance,
]);

export function toAppRole(role: Role | null | undefined): AppRole | null {
  switch (role) {
    case Role.Admin:
      return "ADMIN";
    case Role.Operator:
      return "OPERATOR";
    case Role.Finance:
      return "FINANCE";
    default:
      return null;
  }
}

export type PageKey =
  | "dashboard"
  | "users-list"
  | "user-deletion-request"
  | "kyc"
  | "contracts"
  | "mscd-accounts"
  | "kyc-questionnaire"
  | "bonds"
  | "stocks"
  | "savings"
  | "portfolios"
  | "orders"
  | "orderlists"
  | "balances"
  | "huulga"
  | "stock-dividends"
  | "unmatched-deposits"
  | "refund-orders"
  | "refund-list"
  | "refund-calculation"
  | "reports-interest"
  | "reports-fee"
  | "reports-monthly-fee"
  | "reports-corrections"
  | "reports-hold-release"
  | "surveys"
  | "faq"
  | "configs"
  | "images"
  | "activity"
  | "suggest";

export type Permission = {
  view: boolean;
  edit: boolean;
};

const HIDDEN: Permission = { view: false, edit: false };
const VIEW: Permission = { view: true, edit: false };
const EDIT: Permission = { view: true, edit: true };

type Matrix = Record<PageKey, Record<AppRole, Permission>>;

export const PERMISSIONS: Matrix = {
  dashboard: { FINANCE: VIEW, OPERATOR: VIEW, ADMIN: EDIT },
  "users-list": { FINANCE: VIEW, OPERATOR: EDIT, ADMIN: EDIT },
  "user-deletion-request": { FINANCE: HIDDEN, OPERATOR: VIEW, ADMIN: VIEW },
  kyc: { FINANCE: VIEW, OPERATOR: VIEW, ADMIN: EDIT },
  contracts: { FINANCE: VIEW, OPERATOR: VIEW, ADMIN: EDIT },
  "mscd-accounts": { FINANCE: VIEW, OPERATOR: VIEW, ADMIN: EDIT },
  "kyc-questionnaire": { FINANCE: VIEW, OPERATOR: VIEW, ADMIN: EDIT },
  bonds: { FINANCE: VIEW, OPERATOR: VIEW, ADMIN: EDIT },
  stocks: { FINANCE: VIEW, OPERATOR: VIEW, ADMIN: EDIT },
  savings: { FINANCE: HIDDEN, OPERATOR: HIDDEN, ADMIN: EDIT },
  portfolios: { FINANCE: VIEW, OPERATOR: VIEW, ADMIN: EDIT },
  orders: { FINANCE: VIEW, OPERATOR: EDIT, ADMIN: EDIT },
  orderlists: { FINANCE: VIEW, OPERATOR: EDIT, ADMIN: EDIT },
  balances: { FINANCE: EDIT, OPERATOR: EDIT, ADMIN: EDIT },
  huulga: { FINANCE: EDIT, OPERATOR: EDIT, ADMIN: EDIT },
  "stock-dividends": { FINANCE: VIEW, OPERATOR: EDIT, ADMIN: EDIT },
  "unmatched-deposits": { FINANCE: EDIT, OPERATOR: EDIT, ADMIN: EDIT },
  "refund-orders": { FINANCE: VIEW, OPERATOR: EDIT, ADMIN: EDIT },
  "refund-list": { FINANCE: VIEW, OPERATOR: EDIT, ADMIN: EDIT },
  "refund-calculation": { FINANCE: HIDDEN, OPERATOR: VIEW, ADMIN: EDIT },
  "reports-interest": { FINANCE: VIEW, OPERATOR: HIDDEN, ADMIN: EDIT },
  "reports-fee": { FINANCE: VIEW, OPERATOR: HIDDEN, ADMIN: EDIT },
  "reports-monthly-fee": { FINANCE: VIEW, OPERATOR: HIDDEN, ADMIN: EDIT },
  "reports-corrections": { FINANCE: VIEW, OPERATOR: HIDDEN, ADMIN: EDIT },
  "reports-hold-release": { FINANCE: VIEW, OPERATOR: HIDDEN, ADMIN: EDIT },
  surveys: { FINANCE: HIDDEN, OPERATOR: HIDDEN, ADMIN: EDIT },
  faq: { FINANCE: VIEW, OPERATOR: VIEW, ADMIN: EDIT },
  configs: { FINANCE: HIDDEN, OPERATOR: HIDDEN, ADMIN: EDIT },
  images: { FINANCE: HIDDEN, OPERATOR: HIDDEN, ADMIN: EDIT },
  activity: { FINANCE: HIDDEN, OPERATOR: HIDDEN, ADMIN: EDIT },
  suggest: { FINANCE: HIDDEN, OPERATOR: HIDDEN, ADMIN: EDIT },
};

export function getPermission(role: AppRole, key: PageKey): Permission {
  return PERMISSIONS[key]?.[role] ?? HIDDEN;
}

export function canView(role: AppRole | null, key: PageKey): boolean {
  if (!role) return false;
  return getPermission(role, key).view;
}

export function canEdit(role: AppRole | null, key: PageKey): boolean {
  if (!role) return false;
  return getPermission(role, key).edit;
}

export function rolesWithView(key: PageKey): Role[] {
  const result: Role[] = [];
  if (PERMISSIONS[key].FINANCE.view) result.push(Role.Finance);
  if (PERMISSIONS[key].OPERATOR.view) result.push(Role.Operator);
  if (PERMISSIONS[key].ADMIN.view) result.push(Role.Admin);
  return result;
}

export function rolesWithEdit(key: PageKey): Role[] {
  const result: Role[] = [];
  if (PERMISSIONS[key].FINANCE.edit) result.push(Role.Finance);
  if (PERMISSIONS[key].OPERATOR.edit) result.push(Role.Operator);
  if (PERMISSIONS[key].ADMIN.edit) result.push(Role.Admin);
  return result;
}
